import { nanoid } from '@reduxjs/toolkit';

import { forwardRef, useEffect, useMemo, useState } from 'react';

import Skeleton from './Skeleton';
import * as S from './styled/YoutubePlayer.styled';

/**
 * @description YoutubePlayer is a component that is used to display a youtube video.
 * @param {string} videoId - The id of the video
 * @param {number} progress - The progress of the video
 * @param {boolean} autoplay - If the video should autoplay
 * @param {boolean} controls - If the video should have controls
 * @param {number} volume - The volume of the video
 * @param {function} onProgress - The function that will be called when the video progress changes
 * @param {function} onPlay - The function that will be called when the video starts playing
 * @param {function} onPause - The function that will be called when the video is paused
 * @param {function} onReady - The function that will be called when the video is ready
 * @param {function} onEnded - The function that will be called when the video is ended
 * @returns {JSX.Element} - YoutubePlayer component
 * @constructor
 * @example
 * <YoutubePlayer
 * 	videoId="213asdd"
 * />
 */
const YoutubePlayer = forwardRef((props, ref) => {
	const { videoId, progress, autoplay, controls, volume, onProgress, onPlay, onPause, onReady, onEnded } = props;

	const [player, setPlayer] = useState(null);
	const [loading, setLoading] = useState(true);

	const id = useMemo(() => nanoid(), []);

	const playerReady = (event) => {
		setLoading(false);
		onReady && onReady(event);

		setPlayer(event.target);
		ref?.current && (ref.current = event.target);

		volume && event.target.setVolume(volume);
	};

	const onPlayerStateChange = (event) => {
		switch (event.data) {
			case window.YT.PlayerState.PLAYING:
				onPlay && onPlay();
				break;
			case window.YT.PlayerState.PAUSED:
				onPause && onPause();
				break;
			case window.YT.PlayerState.ENDED:
				onEnded && onEnded();
		}
		onProgress && onProgress(event.target.getCurrentTime());
	};

	const loadVideo = () => {
		window.monetizeVideo = new window.YT.Player(`player-${id}`, {
			videoId,
			playerVars: {
				width: '100%',
				height: '100%',
				controls: controls ? 1 : 0,
				disablekb: controls ? 0 : 1,
				rel: 0
			},
			events: {
				onReady: playerReady,
				onStateChange: onPlayerStateChange
			}
		});
	};

	useEffect(() => {
		if (!window.YT) {
			const tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';

			window.onYouTubeIframeAPIReady = loadVideo;

			const firstScriptTag = document.getElementsByTagName('script')[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
		} else {
			loadVideo();
		}
		return () => {
			player?.destroy();
		};
	}, []);

	useEffect(() => {
		progress && player?.seekTo(progress);
	}, [progress, player]);

	useEffect(() => {
		autoplay && player?.playVideo();
	}, [autoplay, player]);

	return (
		<S.PlayerWrapper>
			{loading && (
				<Skeleton
					width="100%"
					height="100%"
					style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, borderRadius: 0 }}
				/>
			)}
			<div id={`player-${id}`} />
		</S.PlayerWrapper>
	);
});

YoutubePlayer.displayName = 'YoutubePlayer';
export default YoutubePlayer;
